import { DialogChat } from "../../../types/DialogChat";

export type GlobalChatConversationKind =
  | "direct"
  | "group"
  | "webinar"
  | "support";

export type GlobalChatConversationActionId =
  | "pin"
  | "unpin"
  | "mute"
  | "unmute"
  | "block"
  | "unblock"
  | "leave"
  | "participants";

export type GlobalChatConversationActionAvailability = Record<
  GlobalChatConversationActionId,
  boolean
>;

export type GlobalChatConversationSections = {
  pinned: DialogChat[];
  unpinned: DialogChat[];
};

export const includesUserId = (userIds: string[] | undefined, userId?: string) =>
  Boolean(userId && (userIds || []).includes(userId));

export const setUserIdInList = (
  userIds: string[] | undefined,
  userId: string,
  shouldInclude: boolean,
) => {
  const existingIds = userIds || [];

  if (shouldInclude) {
    return existingIds.includes(userId) ? existingIds : [...existingIds, userId];
  }

  return existingIds.filter((id) => id !== userId);
};

export const getGlobalChatConversationKind = (
  chat: DialogChat,
  adminUserId?: string,
): GlobalChatConversationKind => {
  const isSupportChat =
    Boolean(adminUserId) &&
    chat.members.length === 1 &&
    chat.members[0]?._id === adminUserId;

  if (isSupportChat) {
    return "support";
  }

  if (chat.webinar) {
    return "webinar";
  }

  if (chat.isGroup || chat.members.length > 2 || chat.title) {
    return "group";
  }

  return "direct";
};

export const isGlobalChatConversationPinned = (
  chat: DialogChat,
  currentUserId?: string,
) => includesUserId(chat.pinnedBy, currentUserId);

export const groupGlobalChatConversations = (
  conversations: DialogChat[],
  currentUserId?: string,
): GlobalChatConversationSections =>
  conversations.reduce<GlobalChatConversationSections>(
    (sections, chat) => {
      const section = isGlobalChatConversationPinned(chat, currentUserId)
        ? sections.pinned
        : sections.unpinned;

      section.push(chat);
      return sections;
    },
    { pinned: [], unpinned: [] },
  );

export const getGlobalChatConversationActionAvailability = (
  chat: DialogChat,
  params: {
    currentUserId?: string;
    adminUserId?: string;
  },
): GlobalChatConversationActionAvailability => {
  const kind = getGlobalChatConversationKind(chat, params.adminUserId);
  const isPinned = isGlobalChatConversationPinned(chat, params.currentUserId);
  const isMuted = includesUserId(chat.mutedBy, params.currentUserId);
  const isBlocked = includesUserId(chat.blockedBy, params.currentUserId);
  const canBlock = kind === "direct";
  const canLeave = kind === "group" || kind === "webinar";
  const canViewParticipants = kind === "group" || kind === "webinar";

  return {
    pin: !isPinned,
    unpin: isPinned,
    mute: !isMuted,
    unmute: isMuted,
    block: canBlock && !isBlocked,
    unblock: canBlock && isBlocked,
    leave: canLeave,
    participants: canViewParticipants,
  };
};
